import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Switch,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormControlLabel,
    RadioGroup,
    Radio,
    Button,
    Skeleton
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toggleLoader } from '../../store/loader/loaderReducer';
import axios from 'axios';
import { toast } from 'react-toastify';
import AppHeader from '../../component/appHeader/AppHeader';

const SMTPSettings = () => {
    const [enableCustomSMTP, setEnableCustomSMTP] = useState(true);
    const [port, setPort] = useState('465');
    const [encryptionType, setEncryptionType] = useState('1');
    const [loading, setLoading] = useState(true);
    const [smtpServer, setSmtpServer] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");

    const dispatch = useDispatch();
    const { OrgId } = useSelector((state) => state?.document?.value);

    useEffect(() => {
        if (OrgId) {
            setLoading(true);
            dispatch(toggleLoader(true));
            axios.get(process.env.REACT_APP_SMTP_CONFIG, {
                withCredentials: process.env.REACT_APP_CREDENTIALS, params: { org_id: OrgId }
            })
                .then((res) => {
                    dispatch(toggleLoader(false));
                    setLoading(false);
                    const responseData = res.data.data;
                    setEnableCustomSMTP(responseData?.enable_custom_smtp || false);
                    setSmtpServer(responseData?.host || "");
                    setPort(responseData?.port ? String(responseData.port) : "465");
                    setEncryptionType(responseData?.encryption_type || "1");
                    setUsername(responseData?.username || "");
                    setPassword(responseData?.password || "");
                })
                .catch((err) => {
                    dispatch(toggleLoader(false));
                    toast.error(err.message);
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, []);

    const handleSave = () => {
        dispatch(toggleLoader(true));
        const data = {
            org_id: OrgId,
            enable_custom_smtp: enableCustomSMTP ? 1 : 0,
            host: smtpServer,
            port: port,
            encryption_type: encryptionType,
            username: username,
            password: password,
        };

        const method = smtpServer?.trim() ? "put" : "post";

        axios[method](process.env.REACT_APP_SMTP_CONFIG, data, { withCredentials: process.env.REACT_APP_CREDENTIALS })
            .then(() => {
                toast.success(`SMTP settings ${method === "post" ? "created" : "updated"} successfully`);
                dispatch(toggleLoader(false));
            })
            .catch((err) => {
                toast.error(err.message || `Failed to ${method === "post" ? "create" : "update"} SMTP settings`);
                dispatch(toggleLoader(false));
            });
    };

    return (
        <Box sx={{ padding: '0px 30px 30px' }}>
            <AppHeader
                moduleText='Simple Mail Transfer Protocol (SMTP)'
                rolePermissions={true}
                checkAdmin={true}
            />
            <Typography variant="body2" fontFamily="var(--primary-font-family)" sx={{ mb: 3, mt: 2, color: "var(--table-header-font)" }}>
                Enter the details of your SMTP server for outgoing emails from this account.
            </Typography>

            {loading ? (
                <>
                    {[...Array(6)].map((_, idx) => (
                        <Row key={idx} label={<Skeleton width={200} height={20} />}>
                            <Skeleton variant="rectangular" width="50%" height={40} />
                        </Row>
                    ))}
                </>
            ) : (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enableCustomSMTP}
                                onChange={(e) => setEnableCustomSMTP(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontFamily: "var(--primary-font-family)",
                                    fontWeight: 500,
                                    color: "var(--table-font-color)",
                                }}
                            >
                                Enable custom SMTP
                            </Typography>
                        }
                        sx={{ mb: 3, fontSize: 14 }}
                    />

                    <Row label="SMTP server address">
                        <TextField
                            value={smtpServer}
                            onChange={(e) => setSmtpServer(e.target.value)}
                            sx={{
                                width: "50%",
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "#ccc" },
                                    "&:hover fieldset": { borderColor: "#ccc" },
                                    "&.Mui-focused fieldset": { borderColor: "#ccc" },
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "14px",
                                    fontFamily: "var(--primary-font-family)",
                                },
                            }}
                            size="small"
                        />
                    </Row>

                    <Row label="Port">
                        <FormControl size="small" sx={{ width: "50%" }}>
                            <Select
                                value={port}
                                onChange={(e) => setPort(e.target.value)}
                                sx={{
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ccc",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ccc",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ccc",
                                    },
                                    fontSize: "14px",
                                    fontFamily: "Arial, sans-serif",
                                }}
                            >
                                <MenuItem value="25">25</MenuItem>
                                <MenuItem value="587">587</MenuItem>
                                <MenuItem value="465">465</MenuItem>
                            </Select>
                        </FormControl>
                    </Row>

                    <Row label="Connection encryption type">
                        <RadioGroup
                            row
                            value={encryptionType}
                            onChange={(e) => setEncryptionType(e.target.value)}
                        >
                            <FormControlLabel value="1" control={<Radio />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', fontFamily: "var(--primary-font-family)", color: ("var(--table-font-color)") } }} label="SSL" />
                            <FormControlLabel value="2" control={<Radio />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', fontFamily: "var(--primary-font-family)", color: ("var(--table-font-color)") } }} label="TLS" />
                            <FormControlLabel value="3" control={<Radio />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', fontFamily: "var(--primary-font-family)", color: ("var(--table-font-color)") } }} label="PLAIN" />
                        </RadioGroup>
                    </Row>

                    <Row label="Username">
                        <TextField
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                            inputProps={{ 'data-1p-ignore': true }}
                            sx={{
                                width: "50%",
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "#ccc" },
                                    "&:hover fieldset": { borderColor: "#ccc" },
                                    "&.Mui-focused fieldset": { borderColor: "#ccc" },
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "14px",
                                    fontFamily: "var(--primary-font-family)",
                                },
                            }}
                            size="small"
                        />
                    </Row>

                    <Row label="Password">
                        <TextField
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            type="password"
                            autoComplete="new-password"
                            spellCheck={false}
                            inputProps={{ 'data-1p-ignore': true }}
                            sx={{
                                width: "50%",
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "#ccc" },
                                    "&:hover fieldset": { borderColor: "#ccc" },
                                    "&.Mui-focused fieldset": { borderColor: "#ccc" },
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "14px",
                                    fontFamily: "var(--primary-font-family)",
                                },
                            }}
                            size="small"
                        />
                    </Row>
                </>
            )}

            {!loading && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 30,
                        left: 110,
                        zIndex: 1000,
                    }}
                >
                    <Button
                        onClick={handleSave}
                        type="submit"
                        variant="contained"
                        sx={{ textTransform: "capitalize", width: "90px" }}
                    >
                        Save
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default SMTPSettings;

const Row = ({ label, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 3 }}>
        <Typography sx={{ width: 220, fontSize: 14, color: "var(--table-font-color)", fontFamily: "var(--primary-font-family)" }}>
            {label}
        </Typography>
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
);
